<template>
    <Modal2 v-model="visibleProxy" :title="$t('spheres.widgetPicker.title')" size="lg">
        <template #body>
            <div v-if="loading" class="picker-loading">
                <div class="spinner-border" role="status"></div>
                <p>{{ $t('spheres.widgetPicker.loading') }}</p>
            </div>
            <div v-else-if="!catalogWidgets.length" class="picker-empty">
                <div class="picker-empty-icon">
                    <i class="bi bi-grid-3x3-gap"></i>
                </div>
                <p>{{ $t('spheres.widgetPicker.emptyCatalog') }}</p>
            </div>
            <div v-else-if="!filteredSections.length && !search.trim()" class="picker-empty">
                <div class="picker-empty-icon">
                    <i class="bi bi-check2-circle"></i>
                </div>
                <p>{{ $t('spheres.widgetPicker.allAttached') }}</p>
            </div>
            <div v-else>
                <!-- Search -->
                <div v-if="catalogWidgets.length > 6 || search" class="picker-search">
                    <i class="bi bi-search"></i>
                    <input
                        type="text"
                        v-model="search"
                        :placeholder="$t('spheres.widgetPicker.searchPlaceholder')"
                    />
                    <button v-if="search" type="button" class="picker-search-clear" @click="search = ''">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>

                <div v-if="!filteredSections.length" class="picker-empty">
                    <div class="picker-empty-icon">
                        <i class="bi bi-search"></i>
                    </div>
                    <p>{{ $t('spheres.widgetPicker.noResults', { query: search }) }}</p>
                </div>

                <div v-else>
                    <div
                        v-for="sec in filteredSections"
                        :key="sec.key"
                        class="picker-group"
                    >
                        <div class="picker-section-label">
                            <i :class="'bi bi-' + (sec.icon || 'grid-3x3-gap')"></i>
                            {{ sec.label }}
                            <span class="picker-section-meta">
                                — {{ sec.widgets.length }} {{ pluralize(sec.widgets.length) }}
                            </span>
                        </div>
                        <div class="picker-grid">
                            <label
                                v-for="cw in sec.widgets"
                                :key="cw.id"
                                class="picker-chip"
                                :class="{ 'picker-chip-active': selected.has(cw.id) }"
                            >
                                <input
                                    type="checkbox"
                                    :checked="selected.has(cw.id)"
                                    @change="toggle(cw.id)"
                                />
                                <img
                                    v-if="cw.picture"
                                    :src="displayPicture(cw.picture)"
                                    class="picker-chip-pic"
                                    :alt="cw.title"
                                    @error="onPicError($event)"
                                />
                                <span class="picker-chip-name">{{ cw.title }}</span>
                                <span class="picker-chip-type">{{ cw.type }}</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <button type="button" class="btn-modal-secondary" @click="close">{{ $t('spheres.widgetPicker.actions.cancel') }}</button>
            <button
                type="button"
                class="btn-modal-primary"
                :disabled="!selected.size"
                @click="confirm"
            >
                <i class="bi bi-plus-lg"></i>
                {{ $t('spheres.widgetPicker.actions.add') }}{{ selected.size ? ' (' + selected.size + ')' : '' }}
            </button>
        </template>
    </Modal2>
</template>

<script>
import Modal2 from '../Modal2.vue'

export default {
    name: 'WidgetPickerModal',
    components: { Modal2 },
    props: {
        modelValue: {
            type: Boolean,
            required: true
        },
        sphereWidgets: {
            type: Array,
            required: true
        }
    },
    emits: ['update:modelValue', 'add'],
    data: () => ({
        catalogSections: [],   // [{id, key, label, icon_slug, position, is_hidden}]
        catalogWidgets: [],    // [{id, type, section (key), title, picture, link, position, is_hidden}]
        loading: false,
        selected: new Set(),   // set of catalog_widget.id ints
        search: ''
    }),
    computed: {
        visibleProxy: {
            get() { return this.modelValue },
            set(v) { this.$emit('update:modelValue', v) }
        },
        // catalog_widget.ids already attached to this sphere (instance-level).
        alreadyAttached() {
            const ids = new Set()
            for (const w of (this.sphereWidgets || [])) {
                if (w.catalog_widget_id != null) ids.add(w.catalog_widget_id)
            }
            return ids
        },
        // Available rows = visible + not already attached.
        available() {
            return this.catalogWidgets.filter(w =>
                !w.is_hidden && !this.alreadyAttached.has(w.id)
            )
        },
        // Grouped by section, with search applied. Empty sections are dropped.
        // The /api/catalog response identifies sections by `key` (not `id`)
        // and pairs each widget with its parent section's `key` via the
        // `section` field — so we group on that, not on a synthetic id.
        filteredSections() {
            const q = this.search.trim().toLowerCase()
            const bySection = new Map()
            for (const w of this.available) {
                if (q) {
                    const match =
                        (w.title || '').toLowerCase().includes(q) ||
                        (w.type || '').toLowerCase().includes(q)
                    if (!match) continue
                }
                if (!bySection.has(w.section)) bySection.set(w.section, [])
                bySection.get(w.section).push(w)
            }
            return this.catalogSections
                .filter(s => !s.is_hidden && bySection.has(s.key))
                .map(s => ({ ...s, widgets: bySection.get(s.key) }))
        }
    },
    watch: {
        async modelValue(v) {
            if (v) {
                this.selected = new Set()
                this.search = ''
                await this.loadCatalog()
            }
        }
    },
    methods: {
        async loadCatalog() {
            this.loading = true
            const r = await this.$api.getCatalog()
            this.loading = false
            if (r.result === 'SUCCESS') {
                this.catalogSections = r.data.sections || []
                this.catalogWidgets = r.data.widgets || []
            } else {
                this.$notify({ text: r.msg, type: 'error' })
                this.catalogSections = []
                this.catalogWidgets = []
            }
        },
        toggle(id) {
            if (this.selected.has(id)) {
                this.selected.delete(id)
            } else {
                this.selected.add(id)
            }
            this.selected = new Set(this.selected)
        },
        confirm() {
            // Emit array of { widget_type, catalog_widget_id } so the parent
            // can call addSphereWidget(sphereId, type, id) for each. The
            // /api/catalog response names the type column `type` (not
            // widget_type) so we read it from there but emit the canonical
            // widget_type the spheres API expects.
            const byId = new Map(this.catalogWidgets.map(w => [w.id, w]))
            const out = []
            for (const id of this.selected) {
                const w = byId.get(id)
                if (w) out.push({ widget_type: w.type, catalog_widget_id: w.id, title: w.title })
            }
            this.$emit('add', out)
            this.close()
        },
        close() {
            this.$emit('update:modelValue', false)
        },
        pluralize(n) {
            // 1 виджет / 2-4 виджета / 5+ виджетов
            const n10 = n % 10, n100 = n % 100
            if (n10 === 1 && n100 !== 11) return this.$t('spheres.widgetPicker.plural.widget.one')
            if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return this.$t('spheres.widgetPicker.plural.widget.few')
            return this.$t('spheres.widgetPicker.plural.widget.many')
        },
        // Widget pictures in catalog_widget are stored as cabinet-relative
        // paths (e.g. ../images/widget/menu.svg). They 404 on billing's host;
        // rewrite to absolute cabinet URLs for the admin preview. Mirrors
        // the helper in components/catalog/Catalog.vue.
        displayPicture(p) {
            if (!p) return ''
            if (/^https?:\/\//.test(p)) return p
            if (p.startsWith('data:')) return p
            const cleaned = p.replace(/^(\.\.\/)+/, '/')
            const path = cleaned.startsWith('/') ? cleaned : '/' + cleaned
            return 'https://cabinet.superqr.com' + path
        },
        onPicError(e) { e.target.style.opacity = 0.25 }
    }
}
</script>

<style scoped>
.picker-loading {
    text-align: center;
    padding: 32px 16px;
    color: #64748b;
}
.picker-loading .spinner-border {
    color: #7c3aed;
    width: 1.75rem;
    height: 1.75rem;
}
.picker-loading p {
    margin: 12px 0 0;
    font-size: 13px;
}

.picker-empty {
    text-align: center;
    padding: 32px 16px;
}
.picker-empty-icon {
    width: 48px;
    height: 48px;
    margin: 0 auto 12px;
    background: #f5f0ff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7c3aed;
    font-size: 20px;
}
.picker-empty p {
    color: #64748b;
    font-size: 14px;
    margin: 0;
}

/* Search */
.picker-search {
    position: relative;
    margin-bottom: 14px;
}
.picker-search > i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 14px;
    pointer-events: none;
}
.picker-search input {
    width: 100%;
    padding: 9px 36px 9px 36px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    font-size: 14px;
    color: #1e293b;
    background: #fff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.picker-search input::placeholder {
    color: #94a3b8;
}
.picker-search input:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
}
.picker-search-clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    color: #94a3b8;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
}
.picker-search-clear:hover {
    background: #f1f5f9;
    color: #475569;
}

/* Section label */
.picker-section-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 10px;
}
.picker-section-label i {
    font-size: 14px;
    color: #7c3aed;
}
.picker-section-meta {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 400;
    color: #94a3b8;
    margin-left: 4px;
}

/* Section groups */
.picker-group {
    margin-bottom: 18px;
}
.picker-group:last-child {
    margin-bottom: 0;
}

/* Grid of widgets */
.picker-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 8px;
}
.picker-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: #fff;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.12s ease;
    user-select: none;
    margin: 0;
}
.picker-chip input[type=checkbox] {
    width: 16px;
    height: 16px;
    accent-color: #7c3aed;
    cursor: pointer;
    flex-shrink: 0;
}
.picker-chip-pic {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    object-fit: contain;
    background: #f8fafc;
    flex-shrink: 0;
}
.picker-chip:hover {
    border-color: #c4b5fd;
    background: #faf7ff;
}
.picker-chip-active {
    border-color: #7c3aed;
    background: #f5f0ff;
}
.picker-chip-active .picker-chip-name {
    color: #6d28d9;
}
.picker-chip-name {
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.picker-chip-type {
    font-size: 10px;
    font-family: ui-monospace, monospace;
    color: #94a3b8;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
    flex-shrink: 0;
}
.picker-chip-active .picker-chip-type {
    color: #7c3aed;
    background: #ede9fe;
}

/* Footer buttons */
.btn-modal-primary,
.btn-modal-secondary {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 9px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
}
.btn-modal-primary {
    background: #7c3aed;
    border: 1px solid #7c3aed;
    color: #fff;
    box-shadow: 0 1px 2px rgba(124, 58, 237, 0.2);
}
.btn-modal-primary:hover:not(:disabled) {
    background: #6d28d9;
    border-color: #6d28d9;
}
.btn-modal-primary:disabled {
    background: #c4b5fd;
    border-color: #c4b5fd;
    cursor: not-allowed;
    box-shadow: none;
}
.btn-modal-secondary {
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #475569;
}
.btn-modal-secondary:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}
</style>
